/**
 * ShiftRequestsPanel — manager/admin queue showing pending shift-trade + cover requests.
 * Appears at the bottom of the Schedule page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Repeat, UserCheck, Check, X, Loader2, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { shiftRequestsApi } from '../services/api';
import { useTimeFormat } from '../utils/timeFormat';

export const ShiftRequestsPanel = ({ locationId, onReviewed }) => {
  const fmtTime = useTimeFormat();
  const [tab, setTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await shiftRequestsApi.list({ status: tab === 'all' ? null : tab, locationId });
      setRequests(data || []);
    } catch (e) {
      console.error('Failed to load shift requests:', e);
    } finally {
      setLoading(false);
    }
  }, [tab, locationId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReview = async (decision) => {
    if (!reviewModal) return;
    setProcessing(true);
    try {
      await shiftRequestsApi.review(reviewModal.id, decision, reviewNote || null);
      setReviewModal(null);
      setReviewNote('');
      await load();
      if (onReviewed) onReviewed();
    } catch (e) {
      alert('Failed to review: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const renderShift = (sched) => {
    if (!sched) return <span className="text-gray-400">Shift no longer exists</span>;
    return (
      <span>
        <span className="font-medium">{sched.date}</span>{' '}
        <span className="text-gray-500">{fmtTime.local(sched.start_time)} - {fmtTime.local(sched.end_time)}</span>
        {sched.shift_name && <span className="text-gray-400 ml-1">· {sched.shift_name}</span>}
      </span>
    );
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <Card className="border-0 shadow-md mt-6" data-testid="shift-requests-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Repeat className="w-4 h-4 text-purple-600" />
            Shift Trade & Cover Requests
            {pendingCount > 0 && tab === 'pending' && (
              <Badge className="bg-amber-100 text-amber-700">{pendingCount} pending</Badge>
            )}
          </CardTitle>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="sr-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="sr-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="denied" data-testid="sr-tab-denied">Denied</TabsTrigger>
            <TabsTrigger value="all" data-testid="sr-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 mx-auto animate-spin text-purple-600" /></div>
        ) : requests.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No {tab !== 'all' ? tab : ''} requests</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50" data-testid={`shift-request-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={r.type === 'trade' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                      {r.type === 'trade' ? <Repeat className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                      {r.type === 'trade' ? 'Trade' : 'Cover'}
                    </Badge>
                    <span className="text-sm font-medium">{r.requester?.name || '—'}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <span className="text-sm">{r.target?.name || (r.type === 'cover' ? 'Open cover' : '—')}</span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <div>From: {renderShift(r.requester_schedule)}</div>
                    {r.type === 'trade' && <div>For: {renderShift(r.target_schedule)}</div>}
                    {r.reason && <div className="text-gray-500 italic">&quot;{r.reason}&quot;</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {r.status === 'pending' ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setReviewModal({ ...r, decision: 'denied' }); }} data-testid={`sr-deny-${r.id}`}>
                        <X className="w-4 h-4 mr-1" />Deny
                      </Button>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setReviewModal({ ...r, decision: 'approved' }); }} data-testid={`sr-approve-${r.id}`}>
                        <Check className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={r.status === 'approved' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}>
                      {r.status}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!reviewModal} onOpenChange={(o) => { if (!o) setReviewModal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewModal?.decision === 'approved' ? 'Approve' : 'Deny'} Shift Request
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="text-sm text-gray-600">
              {reviewModal?.type === 'trade' ? 'Trade' : 'Cover'} request from{' '}
              <strong>{reviewModal?.requester?.name}</strong>
              {reviewModal?.target?.name && <> with <strong>{reviewModal.target.name}</strong></>}
            </div>
            {reviewModal?.decision === 'approved' && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                Approving will immediately update the schedule. Audit log entry will be created.
              </div>
            )}
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={2} placeholder="e.g., approved due to coverage" data-testid="sr-review-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal(null)}>Cancel</Button>
            <Button
              onClick={() => handleReview(reviewModal?.decision)}
              disabled={processing}
              className={reviewModal?.decision === 'approved' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="sr-confirm-review"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm {reviewModal?.decision === 'approved' ? 'Approve' : 'Deny'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ShiftRequestsPanel;
